'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import Toggle from '@/components/ui/Toggle';
import { toast } from 'sonner';

interface ProfileFormData {
  displayName: string;
  jobTitle: string;
  timezone: string;
}

export default function ProfileTab() {
  const [avatarHover, setAvatarHover] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [notifications, setNotifications] = useState({
    taskAssigned: true,
    taskDueSoon: true,
    projectUpdates: false,
    weeklyDigest: true,
  });

  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<ProfileFormData>({
    defaultValues: {
      displayName: 'Alex Morgan',
      jobTitle: 'Engineering Lead',
      timezone: 'America/New_York',
    },
  });

  const onSubmit = async (_data: ProfileFormData) => {
    setSaveState('saving');
    // BACKEND INTEGRATION: PUT /api/users/profile with form data
    await new Promise((r) => setTimeout(r, 800));
    setSaveState('saved');
    toast.success('Profile saved');
    setTimeout(() => setSaveState('idle'), 2000);
  };

  return (
    <div className="space-y-5">
      {/* Avatar section */}
      <div className="card-base p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4">Profile Photo</h2>
        <div className="flex items-center gap-4">
          <div
            className="relative w-20 h-20 rounded-2xl cursor-pointer overflow-hidden flex-shrink-0"
            onMouseEnter={() => setAvatarHover(true)}
            onMouseLeave={() => setAvatarHover(false)}
          >
            <div
              className="w-full h-full flex items-center justify-center text-2xl font-bold"
              style={{ background: 'linear-gradient(135deg, #6366F1, #818CF8)', color: '#fff' }}
            >
              AM
            </div>
            {avatarHover && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 fade-in">
                <Icon name="CameraIcon" size={20} className="text-white" />
              </div>
            )}
          </div><div className="flex flex-col gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">Alex Morgan</p>
              <p className="text-xs text-muted-foreground">alex@taskflow.app</p>
            </div>
            <label className="btn-ghost h-8 px-3 text-xs cursor-pointer w-fit">
              <Icon name="ArrowUpTrayIcon" size={14} />
              Upload new photo
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={() => toast.success('Avatar updated')}
              />
            </label>
            <p className="text-xs text-muted-foreground">JPG, PNG or GIF · Max 2MB</p>
          </div>
        </div>
      </div>

      {/* Profile form */}
      <div className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-foreground">Account Details</h2>
          {saveState === 'saving' && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="ArrowPathIcon" size={12} className="animate-spin" />
              Saving…
            </span>
          )}
          {saveState === 'saved' && (
            <span className="flex items-center gap-1.5 text-xs text-accent">
              <Icon name="CheckCircleIcon" size={12} />
              Saved
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Display name */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="display-name">
              Display name
            </label>
            <input
              id="display-name"
              type="text"
              className="input-field"
              {...register('displayName', { required: 'Display name is required', minLength: { value: 2, message: 'Too short' } })}
            />
            {errors.displayName && <p className="mt-1 text-xs text-danger">{errors.displayName.message}</p>}
          </div>

          {/* Email — read-only with verified badge */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="profile-email">
              Email address
            </label>
            <div className="relative">
              <input
                id="profile-email"
                type="email"
                value="alex@taskflow.app"
                readOnly
                className="input-field pr-24 opacity-60 cursor-not-allowed"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs font-medium text-accent">
                <Icon name="CheckBadgeIcon" size={14} className="text-accent" />
                Verified
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Contact support to change your email address</p>
          </div>

          {/* Job title */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="job-title">
              Job title
            </label>
            <input
              id="job-title"
              type="text"
              placeholder="e.g. Senior Engineer"
              className="input-field"
              {...register('jobTitle')}
            />
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="timezone">
              Timezone
            </label>
            <select id="timezone" className="input-field" {...register('timezone')}>
              <option value="America/New_York">Eastern Time (ET) — UTC-5</option>
              <option value="America/Chicago">Central Time (CT) — UTC-6</option>
              <option value="America/Denver">Mountain Time (MT) — UTC-7</option>
              <option value="America/Los_Angeles">Pacific Time (PT) — UTC-8</option>
              <option value="Europe/London">London (GMT) — UTC+0</option>
              <option value="Europe/Berlin">Berlin (CET) — UTC+1</option>
              <option value="Asia/Kolkata">India (IST) — UTC+5:30</option>
              <option value="Asia/Tokyo">Tokyo (JST) — UTC+9</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="submit"
              disabled={!isDirty || saveState === 'saving'}
              className="btn-primary h-9"
            >
              {saveState === 'saving' ? (
                <>
                  <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                  Saving…
                </>
              ) : (
                'Save Profile'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Notification preferences */}
      <div className="card-base p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4">Notification Preferences</h2>
        <div className="space-y-4">
          {[
            { key: 'taskAssigned', label: 'Task assigned to me', description: 'When someone assigns a card to you' },
            { key: 'taskDueSoon', label: 'Task due soon', description: '24 hours before a card you own is due' },
            { key: 'projectUpdates', label: 'Project status changes', description: 'When a project moves to Active, On Hold, or Completed' },
            { key: 'weeklyDigest', label: 'Weekly digest', description: 'Summary of activity across all your projects every Monday' },
          ].map((pref) => (
            <div key={`notif-${pref.key}`} className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">{pref.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{pref.description}</p>
              </div>
              <Toggle
                checked={notifications[pref.key as keyof typeof notifications]}
                onChange={(val) => {
                  setNotifications((prev) => ({ ...prev, [pref.key]: val }));
                  toast.success(`${pref.label} ${val ? 'enabled' : 'disabled'}`);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <div className="card-base p-5">
        <h2 className="text-sm font-semibold text-foreground mb-4">Security</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Password</p>
              <p className="text-xs text-muted-foreground">Last changed 45 days ago</p>
            </div>
            <button className="btn-ghost h-8 px-3 text-xs">
              Change Password
            </button>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Two-factor authentication</p>
              <p className="text-xs text-muted-foreground">Add an extra layer of security to your account</p>
            </div>
            <button className="btn-ghost h-8 px-3 text-xs text-accent">
              Enable 2FA
            </button>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Active sessions</p>
              <p className="text-xs text-muted-foreground">2 active sessions — Chrome on macOS, Safari on iPhone</p>
            </div>
            <button className="btn-ghost h-8 px-3 text-xs text-danger">
              Sign Out All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}